import { HttpClient, HttpParams } from "@angular/common/http";
import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Output,
  EventEmitter,
} from "@angular/core";
import { WebRTCPreview } from "../../../domain/web-r-t-c-preview";
import { takeUntil, timeout } from "rxjs/operators";
import { Subject } from "rxjs";

/**
 * The peer a send was started for. Captured once in sendToPeer and carried through the retries and
 * the POST->GET fallback, because those run after an async boundary and connectionData does not hold
 * still across one: disconnect() nulls it immediately after firing the STOP, and disconnectFrom()
 * swaps it for another connection while it tears the previous one down.
 */
interface PeerAddress {
  from: string;
  to: string;
}

interface SendContext {
  peer: PeerAddress;
  url: string;
  generation: number;
  supportsPost: boolean | null;
  abort: Subject<void> | null;
  startedAt: number;
}

@Directive({
  selector: "[webrtc-src]",
})
export class WebRTCSrcDirective implements OnChanges, OnDestroy {
  @Input("webrtc-src") connectionData: WebRTCPreview;
  /** Runtime-overridable guard for Chrome ICE gatherers that never report completion. */
  @Input() webrtcGatheringSettleMs = 5000;
  /** Opt-in, redacted signalling diagnostics. Disabled in shipped applications. */
  @Input() webrtcDebug = false;
  @Output() refreshChannelPreview = new EventEmitter();

  @Output() videoPlayStarted = new EventEmitter();
  @Output() onConnectionStateChanged = new EventEmitter<string>();
  availabilityZones;
  PingingInterval = null;
  offerRecived = false;
  video: HTMLVideoElement;
  peerConnection: RTCPeerConnection;
  peerConnectionConfig: RTCConfiguration = {
    // The September 2026 platform-team Chrome remediation deliberately uses the Google STUN service
    // alone. In the supplied cross-platform reproduction, adding stun.evrideo.com:6666 suppressed the
    // reflexive candidate and held gathering open for roughly 40 seconds. Restore the first-party
    // server only after the two-server combination is revalidated against the deployed broadcaster.
    iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    iceTransportPolicy: "all",
    iceCandidatePoolSize: 0,
  };
  handingGetAbort = new Subject<void>();
  handingSendAbort = new Subject<void>();
  candidatesBuffer = [];
  remoteStream;

  forceRetryTimeout;
  startHangingGetTimeout;
  remoteDescriptionHasSet = false;
  /** The peer will not take candidates before it has the offer, so they queue until it lands. */
  private offerAccepted = false;
  /** Set when onicecandidate reports a null candidate, i.e. gathering is finished. */
  private gatheringComplete = false;
  private completeSent = false;
  private completionPending = false;
  private answerHandled = false;
  /** Serialises the candidate sends without blocking the gatherer that feeds them. */
  private candidateSendChain: Promise<void> = Promise.resolve();
  /**
   * Chrome only reports gathering complete once every STUN transaction has finished or timed out,
   * and a blackholed server costs the full STUN_TOTAL_TIMEOUT of about 39.75s before that happens.
   * Rather than hold ICECANDIDATESCOMPLETE for it, treat gathering as settled when nothing new has
   * arrived for a while. A healthy network reports complete long before this fires.
   */
  private gatheringSettleTimeout;
  private connectionGeneration = 0;
  private static readonly MAX_SEND_ATTEMPTS = 5;
  private static readonly MAX_OFFER_SEND_ATTEMPTS = 20;
  private static readonly OFFER_RETRY_WINDOW_MS = 30000;
  private static readonly SEND_RETRY_BASE_MS = 200;
  private static readonly MAX_SEND_RETRY_DELAY_MS = 2000;
  private static readonly MAX_HANGING_GET_RETRIES = 5;
  private static readonly HANGING_GET_RETRY_BASE_MS = 250;
  private serverSupportsPost: boolean | null = null; // Cache server capability
  constructor(
    private el: ElementRef,
    private http: HttpClient,
  ) {
    if (this.el.nativeElement.tagName !== "VIDEO") {
      throw new Error("web-rtc-src can be bound only to video element");
    }
    this.video = this.el.nativeElement;
    this.resolveAndConnect();
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes["connectionData"];
    if (!change) {
      return;
    }
    if (change.previousValue !== change.currentValue) {
      // Tear the previous connection down before starting the new one. Without this its hanging GET keeps polling on
      // this same instance, and a late SDP answer for the old client gets applied to the new peer connection - which
      // then ignores its own answer as already established and never receives a stream.
      const currentConnectionData = change.currentValue;
      this.disconnectFrom(change.previousValue).finally(() => {
        if (this.connectionData === currentConnectionData) {
          this.resolveAndConnect();
        }
      });
    }
  }

  /**
   * Runs disconnect() against a connection this directive has already been re-pointed away from, so the STOP goes to
   * the peer being left rather than the one just bound.
   */
  private disconnectFrom(previousConnectionData: WebRTCPreview): Promise<void> {
    if (!previousConnectionData) {
      return Promise.resolve();
    }
    const currentConnectionData = this.connectionData;
    this.connectionData = previousConnectionData;
    const stopped = this.teardownConnection(true);
    this.connectionData = currentConnectionData;
    return stopped;
  }

  resolveAndConnect() {
    if (this.connectionData) {
      this.pauseVideo();
      this.connect();
    }
  }

  pauseVideo() {
    if (this.video.srcObject) {
      (<MediaStream>this.video.srcObject)
        .getTracks()
        .forEach((track) => track.stop());
      this.video.srcObject = null;
    }
  }

  /**
   * Restarted by every candidate. If it fires, gathering has gone quiet and we stop waiting on the
   * browser to admit that a STUN server is never going to answer.
   */
  private armGatheringSettleTimer(generation: number): void {
    clearTimeout(this.gatheringSettleTimeout);
    const settleMs = this.getGatheringSettleMs();
    this.gatheringSettleTimeout = setTimeout(() => {
      if (
        generation !== this.connectionGeneration ||
        this.gatheringComplete ||
        !this.connectionData
      ) {
        return;
      }
      this.debug("ice-gathering-settled", {
        settleMs,
        state: this.peerConnection?.iceGatheringState,
      });
      this.gatheringComplete = true;
      this.flushCandidates(generation);
    }, settleMs);
  }

  private getGatheringSettleMs(): number {
    const configured = Number(this.webrtcGatheringSettleMs);
    return Number.isFinite(configured) &&
      configured >= 1000 &&
      configured <= 30000
      ? configured
      : 5000;
  }

  private debug(event: string, details?: Record<string, unknown>): void {
    if (this.webrtcDebug) {
      console.info(`web-rtc ${event}`, details ?? {});
    }
  }

  createPeerConnection(
    _casterName: string,
    generation: number = this.connectionGeneration,
  ) {
    try {
      // When the channel is TCP-only, skip ICE/STUN gathering entirely for a
      // faster connection init.
      //For this Yondu version: we have an issue with tcpOnlyWebRTC = true and the config is empty. Should be fixed in the next version.
      const config: RTCConfiguration =
        this.connectionData?.tcpOnlyWebRTC && false
          ? { iceServers: [] }
          : this.peerConnectionConfig;
      const peerConnection = new RTCPeerConnection(config);
      this.peerConnection = peerConnection;
      peerConnection.onicegatheringstatechange = () => {
        if (
          generation !== this.connectionGeneration ||
          peerConnection !== this.peerConnection
        ) {
          return;
        }
        if (peerConnection.iceGatheringState === "complete") {
          clearTimeout(this.gatheringSettleTimeout);
        }
        this.debug("ice-gathering-state", {
          state: peerConnection.iceGatheringState,
        });
      };
      peerConnection.onicecandidate = (event) => {
        if (
          generation !== this.connectionGeneration ||
          peerConnection !== this.peerConnection
        ) {
          return;
        }
        if (event.candidate) {
          const line = event.candidate.candidate;
          const candidateType = / typ (\w+)/.exec(line)?.[1] ?? "unknown";
          if (this.completeSent || this.completionPending) {
            this.debug("late-ice-candidate-ignored", {
              type: candidateType,
            });
            return;
          }
          // Chrome masks host addresses with mDNS. The platform-team remediation requires those
          // opaque candidate strings to be forwarded unchanged rather than filtered in the browser.
          this.debug("ice-candidate", {
            type: candidateType,
            mdns: line.includes(".local "),
          });
          this.candidatesBuffer.push({
            sdpMLineIndex: event.candidate.sdpMLineIndex,
            sdpMid: event.candidate.sdpMid,
            candidate: event.candidate.candidate,
          });
          this.armGatheringSettleTimer(generation);
        } else {
          this.debug("ice-gathering-complete", {
            queuedCandidates: this.candidatesBuffer.length,
            offerAccepted: this.offerAccepted,
          });
          // A null candidate is the gatherer saying it is done. This is the only honest moment to
          // send ICECANDIDATESCOMPLETE; it used to go out as soon as the offer was acknowledged,
          // which was almost always before the first candidate existed.
          clearTimeout(this.gatheringSettleTimeout);
          this.gatheringComplete = true;
        }
        this.flushCandidates(generation);
      };

      peerConnection.onconnectionstatechange = (event) =>
        this.onPeerConnectionStateChange(event, generation, peerConnection);
      peerConnection.ontrack = (event) => {
        if (
          generation === this.connectionGeneration &&
          peerConnection === this.peerConnection
        ) {
          const stream = event.streams?.[0] ?? new MediaStream([event.track]);
          this.onRemoteStreamAdded({ stream });
        }
      };
      this.debug("peer-created");
    } catch (e) {
      this.debug("peer-create-failed", { name: e?.name ?? "Error" });
      this.peerConnection = null;
    }
  }

  retryConnection = async () => {
    const conntectionData = this.connectionData;
    await this.teardownConnection(true);
    this.connectionData = conntectionData;
    if (this.connectionData) {
      this.connect();
    }
  };

  /**
   * The offer has been acknowledged, so anything already gathered can go out and anything gathered
   * from here on can follow it.
   *
   * This used to drain the buffer once and, finding it empty, close the peer connection and call
   * disconnect(). Gathering runs against STUN and rarely produces anything inside the offer's round
   * trip, so that empty buffer was the normal case, not a failure - the connection was thrown away
   * before it had a chance. It only ever worked when the offer happened to be slow enough to cover
   * the gathering.
   */
  onOfferAccepted(generation: number = this.connectionGeneration): void {
    if (generation !== this.connectionGeneration) {
      return;
    }
    this.offerAccepted = true;
    this.flushCandidates(generation);
  }

  /**
   * Sends whatever is queued, in gathering order, one request at a time, and closes with
   * ICECANDIDATESCOMPLETE once the gatherer has finished. Re-entrant: every candidate calls this,
   * and the chain keeps them in order without making the gatherer wait on the network.
   */
  private flushCandidates(
    generation: number = this.connectionGeneration,
  ): void {
    if (generation !== this.connectionGeneration || !this.offerAccepted) {
      this.debug("ice-candidates-held", {
        count: this.candidatesBuffer.length,
      });
      return;
    }
    this.candidateSendChain = this.candidateSendChain
      .then(async () => {
        if (generation !== this.connectionGeneration) {
          return;
        }
        while (this.candidatesBuffer.length > 0) {
          if (
            generation !== this.connectionGeneration ||
            !this.connectionData
          ) {
            return;
          }
          // shift, not pop: the earliest candidate is usually the one that connects fastest, and
          // popping sent them in reverse.
          const candidate = this.candidatesBuffer.shift();
          await this.sendToPeer(
            "ICECANDIDATE " + JSON.stringify(candidate),
            "ICECANDIDATE",
          );
        }
        if (
          this.gatheringComplete &&
          !this.completeSent &&
          this.connectionData &&
          generation === this.connectionGeneration
        ) {
          this.completionPending = true;
          try {
            await this.sendToPeer(
              "ICECANDIDATESCOMPLETE",
              "ICECANDIDATESCOMPLETE",
            );
            if (generation === this.connectionGeneration) {
              this.completeSent = true;
            }
          } finally {
            this.completionPending = false;
          }
        }
      })
      .catch((e) => {
        this.debug("ice-send-failed", { name: e?.name ?? "Error" });
        this.failSignalling(generation, "ice-send");
      });
  }

  onPeerConnectionStateChange(
    e,
    generation: number = this.connectionGeneration,
    source: RTCPeerConnection = this.peerConnection,
  ) {
    if (
      generation !== this.connectionGeneration ||
      source !== this.peerConnection
    ) {
      return;
    }
    const connectionState = source?.connectionState;
    if (connectionState) {
      this.onConnectionStateChanged.emit(connectionState);
      this.debug("peer-state", { state: connectionState });

      switch (connectionState) {
        case "connected":
          // The connection has become fully connected
          break;
        case "disconnected":
        case "failed":
          // One or more transports has terminated unexpectedly or in an error
          break;
        case "closed":
          // The connection has been closed
          break;
      }
    }
  }

  onRemoteStreamAdded(event: any) {
    try {
      if (!this.video.srcObject) {
        const stream = event.stream;
        this.remoteStream = stream;
        this.startPlayingStream();
      }
    } catch (e) {
      this.debug("stream-bind-failed", { name: e?.name ?? "Error" });
    }
  }

  startPlayingStream() {
    this.video.srcObject = this.remoteStream;
    // Force muted for the first play() so autoplay is allowed (browser policy: unmuted play requires user gesture).
    const muteState = this.video.muted;
    this.video.muted = true;
    this.video
      .play()
      .then(() => {
        this.video.muted = muteState;
        this.videoPlayStarted.emit();
      })
      .catch((e) => {
        this.debug("autoplay-failed", { name: e?.name ?? "Error" });
        this.video.muted = muteState;
        this.videoPlayStarted.emit();
      });
  }

  handleSDPAnswer(
    SDPAnswer,
    generation: number = this.connectionGeneration,
    peerConnection: RTCPeerConnection = this.peerConnection,
  ) {
    if (
      !this.connectionData ||
      generation !== this.connectionGeneration ||
      !peerConnection ||
      peerConnection !== this.peerConnection
    ) {
      return;
    }

    peerConnection
      .setRemoteDescription(new RTCSessionDescription(SDPAnswer))
      .then(
        () => {
          if (
            generation === this.connectionGeneration &&
            peerConnection === this.peerConnection
          ) {
            this.remoteDescriptionHasSet = true;
            this.debug("remote-description-set");
          }
        },
        (e) => {
          this.debug("remote-description-failed", {
            name: e?.name ?? "Error",
          });
          this.failSignalling(generation, "remote-description");
        },
      );
  }

  startHangingGet(
    generation: number = this.connectionGeneration,
    retryCount = 0,
  ) {
    const connection = this.connectionData;
    if (!connection || generation !== this.connectionGeneration) {
      return;
    }
    try {
      const options = {
        params: new HttpParams().set("waiter", connection.clientId),
        observe: "response",
      };
      const url = `${connection.availabilityZoneUrls[0]}/wait`;
      const abort = this.handingGetAbort;
      this.http
        .get(url, options as any)
        .pipe(takeUntil(abort))
        .subscribe(
          (response: any) => {
            if (generation !== this.connectionGeneration) {
              return;
            }
            if (response.status !== 200) {
              this.debug("wait-http-error", { status: response.status });
            } else if (response.body.result === "OK") {
              if (
                response.body.type === "answer" &&
                this.remoteDescriptionHasSet === false &&
                this.answerHandled === false
              ) {
                this.answerHandled = true;
                this.offerRecived = true;
                clearInterval(this.PingingInterval);
                this.PingingInterval = setInterval(() => {
                  if (generation === this.connectionGeneration) {
                    this.sendToPeer("PING", "PING").catch(() => undefined);
                  }
                }, 5000);
                this.handleSDPAnswer(
                  JSON.parse(response.body.msg),
                  generation,
                  this.peerConnection,
                );
              }
            } else if (response.body.result === "TIMEOUT") {
              this.debug("wait-timeout");
            } else {
              this.debug("wait-rejected");
            }
            this.scheduleHangingGet(generation, 0, 0);
          },
          (error) => {
            if (generation !== this.connectionGeneration) {
              return;
            }
            this.debug("wait-request-failed", {
              status: error?.status ?? "unknown",
              retry: retryCount,
            });
            if (retryCount >= WebRTCSrcDirective.MAX_HANGING_GET_RETRIES - 1) {
              this.failSignalling(generation, "wait");
              return;
            }
            const delay =
              WebRTCSrcDirective.HANGING_GET_RETRY_BASE_MS *
              Math.pow(2, retryCount);
            this.scheduleHangingGet(generation, retryCount + 1, delay);
          },
        );
    } catch (e) {
      this.debug("wait-setup-failed", { name: e?.name ?? "Error" });
      this.failSignalling(generation, "wait-setup");
    }
  }

  private scheduleHangingGet(
    generation: number,
    retryCount: number,
    delay: number,
  ): void {
    clearTimeout(this.startHangingGetTimeout);
    this.startHangingGetTimeout = setTimeout(() => {
      if (generation === this.connectionGeneration) {
        this.startHangingGet(generation, retryCount);
      }
    }, delay);
  }

  disposeHangingGet() {
    if (this.handingGetAbort) {
      this.handingGetAbort.next();
      this.handingGetAbort.complete();
    }

    if (this.handingSendAbort) {
      this.handingSendAbort.next();
      this.handingSendAbort.complete();
    }

    // next() on a completed subject is a no-op, so reusing these would leave every request of a later connection on
    // this instance unabortable - the reason a stale hanging GET could outlive the connection it belonged to.
    this.handingGetAbort = new Subject<void>();
    this.handingSendAbort = new Subject<void>();
  }

  sendToPeer(data, requestName: string, callback = null): Promise<void> {
    const connection = this.connectionData;
    if (!connection) {
      return Promise.resolve();
    }
    const context: SendContext = {
      peer: {
        from: connection.clientId,
        to: connection.brodcasterId,
      },
      url: `${connection.availabilityZoneUrls[0]}/send`,
      generation: this.connectionGeneration,
      supportsPost: this.serverSupportsPost,
      abort: this.handingSendAbort,
      startedAt: performance.now(),
    };
    return this.sendToPeerWithContext(data, requestName, callback, context, 0);
  }

  private sendToPeerWithContext(
    data: any,
    requestName: string,
    callback: any,
    context: SendContext,
    attempt: number,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (
        requestName !== "STOP" &&
        context.generation !== this.connectionGeneration
      ) {
        resolve();
        return;
      }
      if (!this.hasRetryBudget(requestName, attempt, context)) {
        reject(new Error(`${requestName} failed after bounded retries.`));
        return;
      }
      try {
        this.debug("signal-send", { requestName, attempt });
        if (context.supportsPost !== false) {
          this.tryPostRequest(
            data,
            requestName,
            callback,
            resolve,
            reject,
            context,
            attempt,
          );
        } else {
          this.tryGetRequest(
            data,
            requestName,
            callback,
            resolve,
            reject,
            context,
            attempt,
          );
        }
      } catch (e) {
        this.debug("signal-send-failed", {
          requestName,
          name: e?.name ?? "Error",
        });
        if (requestName === "OFFER") {
          this.retrySend(
            data,
            requestName,
            callback,
            context,
            attempt,
            resolve,
            reject,
          );
          return;
        }
        reject(e);
      }
    });
  }

  private tryPostRequest(
    data: any,
    requestName: string,
    callback: any,
    resolve: any,
    reject: any,
    context: SendContext,
    attempt: number,
  ): void {
    const httpParams = new HttpParams()
      .set("from", context.peer.from)
      .set("to", context.peer.to);

    const postBody = btoa(data);

    const options = {
      params: httpParams,
      headers: {
        "Content-Encoding": "base64",
        "Content-Type": "text/plain",
      },
      responseType: "text",
      observe: "response",
    };

    const timeoutMs = context.supportsPost === null ? 5000 : 0;

    const request = this.http.post(context.url, postBody, options as any);
    const postRequest = context.abort
      ? request.pipe(takeUntil(context.abort))
      : request;
    const boundedPostRequest =
      timeoutMs > 0 ? postRequest.pipe(timeout(timeoutMs)) : postRequest;

    boundedPostRequest.subscribe(
      (response: any) => {
        this.handleSuccessResponse(
          response,
          requestName,
          data,
          callback,
          resolve,
          reject,
          context,
          attempt,
          true,
        );
      },
      (err) => {
        this.handlePostError(
          err,
          data,
          requestName,
          callback,
          resolve,
          reject,
          context,
          attempt,
        );
      },
      () => resolve(),
    );
  }

  private tryGetRequest(
    data: any,
    requestName: string,
    callback: any,
    resolve: any,
    reject: any,
    context: SendContext,
    attempt: number,
  ): void {
    const httpParams = new HttpParams()
      .set("from", context.peer.from)
      .set("to", context.peer.to)
      .set("msg", encodeURIComponent(data));

    const options = {
      params: httpParams,
      responseType: "text",
      observe: "response",
    };

    const request = this.http.get(context.url, options as any);
    const getRequest = context.abort
      ? request.pipe(takeUntil(context.abort))
      : request;
    getRequest.subscribe(
      (response: any) => {
        this.handleSuccessResponse(
          response,
          requestName,
          data,
          callback,
          resolve,
          reject,
          context,
          attempt,
        );
      },
      (err) => {
        this.handleGetError(
          err,
          data,
          requestName,
          callback,
          resolve,
          reject,
          context,
          attempt,
        );
      },
      () => resolve(),
    );
  }

  private handleSuccessResponse(
    response: any,
    requestName: string,
    data: any,
    callback: any,
    resolve: any,
    reject: any,
    context: SendContext,
    attempt: number,
    isPostRequest: boolean = false,
  ): void {
    if (response.ok || (response.status >= 200 && response.status < 300)) {
      if (isPostRequest && context.supportsPost === null) {
        context.supportsPost = true;
        if (context.generation === this.connectionGeneration) {
          this.serverSupportsPost = true;
        }
        this.debug("post-capability-confirmed");
      }

      this.debug("signal-send-succeeded", { requestName });
      if (callback) {
        callback.bind(this)(response.body);
      }
      resolve();
    } else {
      reject(new Error(`${requestName} returned HTTP ${response.status}.`));
    }
  }

  private handlePostError(
    err: any,
    data: any,
    requestName: string,
    callback: any,
    resolve: any,
    reject: any,
    context: SendContext,
    attempt: number,
  ): void {
    if ([405, 415].includes(err.status)) {
      this.debug("post-not-supported", { requestName, status: err.status });
      context.supportsPost = false;
      if (context.generation === this.connectionGeneration) {
        this.serverSupportsPost = false;
      }
      this.tryGetRequest(
        data,
        requestName,
        callback,
        resolve,
        reject,
        context,
        attempt,
      );
    } else if (err.status === 404) {
      this.retrySend(
        data,
        requestName,
        callback,
        context,
        attempt,
        resolve,
        reject,
      );
    } else {
      this.handleCommonError(
        err,
        data,
        requestName,
        callback,
        resolve,
        reject,
        context,
        attempt,
      );
    }
  }

  private handleGetError(
    err: any,
    data: any,
    requestName: string,
    callback: any,
    resolve: any,
    reject: any,
    context: SendContext,
    attempt: number,
  ): void {
    if (err.status === 404) {
      this.retrySend(
        data,
        requestName,
        callback,
        context,
        attempt,
        resolve,
        reject,
      );
    } else {
      this.handleCommonError(
        err,
        data,
        requestName,
        callback,
        resolve,
        reject,
        context,
        attempt,
      );
    }
  }

  private handleCommonError(
    err: any,
    data: any,
    requestName: string,
    callback: any,
    resolve: any,
    reject: any,
    context: SendContext,
    attempt: number,
  ): void {
    this.debug("signal-request-failed", {
      requestName,
      status: err?.status ?? "unknown",
    });
    if (requestName === "OFFER") {
      this.retrySend(
        data,
        requestName,
        callback,
        context,
        attempt,
        resolve,
        reject,
      );
    } else {
      reject(err);
    }
  }

  private retrySend(
    data: any,
    requestName: string,
    callback: any,
    context: SendContext,
    attempt: number,
    resolve: any,
    reject: any,
  ): void {
    const nextAttempt = attempt + 1;
    if (!this.hasRetryBudget(requestName, nextAttempt, context)) {
      reject(new Error(`${requestName} failed after bounded retries.`));
      return;
    }
    this.waitBeforeRetry(attempt)
      .then(() => {
        if (
          requestName !== "STOP" &&
          context.generation !== this.connectionGeneration
        ) {
          resolve();
          return;
        }
        return this.sendToPeerWithContext(
          data,
          requestName,
          callback,
          context,
          nextAttempt,
        ).then(resolve, reject);
      })
      .catch(reject);
  }

  private waitBeforeRetry(attempt: number): Promise<void> {
    const delay = Math.min(
      WebRTCSrcDirective.SEND_RETRY_BASE_MS * Math.pow(2, attempt),
      WebRTCSrcDirective.MAX_SEND_RETRY_DELAY_MS,
    );
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  private hasRetryBudget(
    requestName: string,
    attempt: number,
    context: SendContext,
  ): boolean {
    if (requestName !== "OFFER") {
      return attempt < WebRTCSrcDirective.MAX_SEND_ATTEMPTS;
    }
    return (
      attempt < WebRTCSrcDirective.MAX_OFFER_SEND_ATTEMPTS &&
      performance.now() - context.startedAt <
        WebRTCSrcDirective.OFFER_RETRY_WINDOW_MS
    );
  }

  connect() {
    const connection = this.connectionData;
    if (!connection) {
      return;
    }

    const generation = ++this.connectionGeneration;
    this.debug("connect-started");
    this.remoteDescriptionHasSet = false;
    this.answerHandled = false;
    this.offerAccepted = false;
    this.gatheringComplete = false;
    this.completeSent = false;
    this.completionPending = false;
    this.candidateSendChain = Promise.resolve();
    this.startHangingGet(generation);

    this.createPeerConnection(connection.brodcasterId, generation);
    const peerConnection = this.peerConnection;
    if (!peerConnection) {
      this.failSignalling(generation, "peer-create");
      return;
    }
    peerConnection
      .createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      })
      .then(async (sessionDescription) => {
        if (
          generation !== this.connectionGeneration ||
          peerConnection !== this.peerConnection
        ) {
          return;
        }
        await peerConnection.setLocalDescription(sessionDescription);
        if (
          generation !== this.connectionGeneration ||
          peerConnection !== this.peerConnection
        ) {
          return;
        }
        this.debug("local-description-set");
        // Arm it here too, so gathering that produces nothing at all is still bounded.
        this.armGatheringSettleTimer(generation);
        const SDPOffer = JSON.stringify(sessionDescription);

        await this.sendToPeer("OFFER " + SDPOffer, "OFFER", (response) => {
          if (generation !== this.connectionGeneration) {
            return;
          }
          this.debug("offer-accepted", {
            queuedCandidates: this.candidatesBuffer.length,
          });
          this.onOfferAccepted(generation);
        });

        //this.forceRetryTimeout = setTimeout(() => this.forceRetries(), 5000);
      })
      .catch((error) => {
        if (generation === this.connectionGeneration) {
          this.debug("connection-setup-failed", {
            name: error?.name ?? "Error",
          });
          this.failSignalling(generation, "offer");
        }
      });
  }
  disconnect() {
    void this.teardownConnection(true);
  }

  private teardownConnection(sendStop: boolean): Promise<void> {
    const connectionToStop = this.connectionData;
    const stopCapability = this.serverSupportsPost;
    const stopGeneration = ++this.connectionGeneration;
    const peerConnection = this.peerConnection;
    this.pauseVideo();
    clearInterval(this.PingingInterval);
    clearTimeout(this.forceRetryTimeout);
    clearTimeout(this.startHangingGetTimeout);
    clearTimeout(this.gatheringSettleTimeout);
    this.PingingInterval = null;
    this.offerRecived = false;
    this.remoteDescriptionHasSet = false;
    this.answerHandled = false;
    // Candidates gathered for the connection being dropped must not be offered to the next one.
    this.candidatesBuffer = [];
    this.offerAccepted = false;
    this.gatheringComplete = false;
    this.completeSent = false;
    this.completionPending = false;
    // Abort the old connection's requests before the STOP, not after: the abort subject signals every request that
    // was subscribed to it, so sending the STOP first only got it cancelled a moment later and the peer was never
    // actually told to stop.
    this.disposeHangingGet();
    this.connectionData = null;
    this.serverSupportsPost = null;
    let stopPromise = Promise.resolve();
    if (sendStop && connectionToStop) {
      const context: SendContext = {
        peer: {
          from: connectionToStop.clientId,
          to: connectionToStop.brodcasterId,
        },
        url: `${connectionToStop.availabilityZoneUrls[0]}/send`,
        generation: stopGeneration,
        supportsPost: stopCapability,
        abort: null,
        startedAt: performance.now(),
      };
      stopPromise = this.sendToPeerWithContext(
        "STOP",
        "STOP",
        null,
        context,
        0,
      ).catch((error) => {
        this.debug("stop-unconfirmed", { name: error?.name ?? "Error" });
      });
    }
    this.peerConnection = null;
    if (peerConnection) {
      peerConnection.close();
    }
    return stopPromise;
  }

  private failSignalling(generation: number, stage: string): void {
    if (generation !== this.connectionGeneration) {
      return;
    }
    this.debug("signalling-failed", { stage });
    const connectionData = this.connectionData;
    void this.teardownConnection(false);
    this.connectionData = connectionData;
    this.onConnectionStateChanged.emit("signalling-failed");
  }

  forceRetries() {
    if (!this.offerRecived) {
      clearInterval(this.PingingInterval);
      this.PingingInterval = null;
      this.peerConnection = null;
      this.connect();
    }
  }
}
